import Axios from "axios";
import { authService } from "../pages/Authentication/authService";
import config from "./Config";

const axiosInstance = Axios.create({
  baseURL: config.BASE_URL,
  withCredentials: true // This ensures cookies are sent with every request
})

// Add request interceptor to include auth token in all requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = authService.getAuthToken();
    if (token) {
      config.headers.Authorization = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
const getTeamId = () => {
  const activeTeam = authService.getActiveTeam();
  return activeTeam?.id;
}
const getTeamName = () => {
  const activeTeam = authService.getActiveTeam();
  return activeTeam?.name;
}

const getAllCampaigns = async ({
  page = 0,
  status = "ACTIVE",
  pageSize = 50,
  sortOrder = "DESC",
  sortFieldBy = "createdAt"
}) => {
  try {
    const teamId = getTeamId();
    const response = await axiosInstance.get(`${config.BENEFIT_ENGINE.BASE}${config.BENEFIT_ENGINE.CAMPAIGN.BASE}`, {
      params: {
        page,
        pageSize,
        sortOrder,
        sortFieldBy,
        queries: `status=eq=${status?.toUpperCase()}&&teamId=eq=${teamId}`
      }
    })
    return response.data
  } catch (err) {
    console.log("getAllCampaigns err: ", err)
    //throw toast here
  }
}

const getAllRules = async ({
  offset = 0,
  status = "PUBLISHED",
  type,
  pageSize = 50,
  sortOrder = "DESC"
}) => {
  try {
    const teamId = getTeamId();
    const response = await axiosInstance.get(`${config.BENEFIT_ENGINE.BASE}/rule`, {
      params: {
        // offset,
        // pageSize,
        // sortOrder,
        queries: `status=eq=${status?.toUpperCase()}${type ? `&&type=eq=${type.toUpperCase()}` : ''}&&teamId=eq=${teamId}`
      }
    })

    return response.data

  } catch (err) {
    console.log("getAllRules err: ", err)
  }
}

const getUIConfig = async ({
  catalogId
}) => {
  try {
    const teamId = getTeamId();
    const response = await axiosInstance.get(`${config.BENEFIT_ENGINE.BASE}${config.BENEFIT_ENGINE.UI_CONFIG.BASE}`, {
      params: {
        catalogId,
        teamId
      }
    })

    return response.data
  } catch (err) {
    console.log("getUiConfig err: ", err)
  }
}

const getUIConfigV2 = async ({
  catalogId,
  catalogType
}) => {
  try {
    const teamId = getTeamId();
    const response = await axiosInstance.post(`${config.BENEFIT_ENGINE.BASE}/catalog/get`, {
      catalogType,
      catalogId,
      teamId
    }, {
      params: {
        teamId
      }
    })

    return response.data
  } catch (err) {
    console.log("getUiConfig err: ", err)
  }
}
// not used anywhere
const createConfig = async ({
  catalogState, // DRAFT | PUBLISHED
  catalogComponent, // UI Config Json
  catalogId
}) => {
  try {
    const teamId = getTeamId();
    const response = await axiosInstance.post(`${config.BENEFIT_ENGINE.BASE}/capture/${catalogId}`, {
      catalogInstanceDetails: {
        catalogState,  // in update call pass "ruleId":[]
        ruleId: [],
        teamId
      },
      catalogComponent
    },{
      params: {
        teamId
      }
    })

    return response.data

  } catch (err) {
    console.log("createConfig err: ", err)
  }
}
// made with reference from (Publish Campaign/Save to Drafts) postman
const createConfigV2 = async ({
  catalogState, // DRAFT | PUBLISHED
  catalogComponent, // UI Config Json
  catalogType
}) => {
  try {
    const teamId = getTeamId();
    const teamName = getTeamName();
    const response = await axiosInstance.post(`${config.BENEFIT_ENGINE.BASE}/capture/${catalogType}`, {
      catalogInstanceDetails: {
        catalogState, 
        ruleId: [], // in update call pass "ruleId":[] (have to check)
        teamId,
        teamName
      },
      catalogComponent
    },{
      params: {
        teamId
      }
    })

    return response.data

  } catch (err) {
    console.log("createConfig err: ", err)
  }
}

// Helper function to find matching node by elementId or keyName
const findMatchingNode = (targetId, targetKeyName, nodes) => {
  if (!nodes || !Array.isArray(nodes)) return null
  
  for (const node of nodes) {
    // Match by elementId first (more specific)
    if (targetId && node.elementId === targetId) {
      return node
    }
    // Fallback to keyName match
    if (targetKeyName && node.keyName === targetKeyName) {
      return node
    }
  }
  return null
}

// Helper function to merge possibleValues from payment catalog into campaign data by matching elementId
const mergePossibleValues = (campaignNode, paymentNode) => {
  if (!campaignNode || !paymentNode) return

  // If both nodes have elementId and they match, merge possibleValues
  if (campaignNode.elementId && paymentNode.elementId && 
      campaignNode.elementId === paymentNode.elementId) {
    if (paymentNode.valueConfig?.possibleValues && 
        Array.isArray(paymentNode.valueConfig.possibleValues) &&
        paymentNode.valueConfig.possibleValues.length > 0) {
      if (!campaignNode.valueConfig) {
        campaignNode.valueConfig = {}
      }
      // Only merge if campaign's possibleValues is empty or doesn't exist
      if (!campaignNode.valueConfig.possibleValues || 
          (Array.isArray(campaignNode.valueConfig.possibleValues) && 
           campaignNode.valueConfig.possibleValues.length === 0)) {
        campaignNode.valueConfig.possibleValues = paymentNode.valueConfig.possibleValues
      }
    }
  }

  // Recursively process children - match by elementId or keyName for better accuracy
  if (campaignNode.children && Array.isArray(campaignNode.children) &&
      paymentNode.children && Array.isArray(paymentNode.children)) {
    campaignNode.children.forEach(campaignChild => {
      const matchingPaymentChild = findMatchingNode(
        campaignChild.elementId, 
        campaignChild.keyName, 
        paymentNode.children
      )
      if (matchingPaymentChild) {
        mergePossibleValues(campaignChild, matchingPaymentChild)
      }
    })
  }

  // Handle multiChildren (for MULTI_ADD componentSubtype)
  if (campaignNode.multiChildren && Array.isArray(campaignNode.multiChildren) &&
      paymentNode.multiChildren && Array.isArray(paymentNode.multiChildren)) {
    campaignNode.multiChildren.forEach((campaignMultiChild, index) => {
      if (paymentNode.multiChildren[index]) {
        campaignMultiChild.forEach(campaignChild => {
          const matchingPaymentChild = findMatchingNode(
            campaignChild.elementId,
            campaignChild.keyName,
            paymentNode.multiChildren[index]
          )
          if (matchingPaymentChild) {
            mergePossibleValues(campaignChild, matchingPaymentChild)
          }
        })
      }
    })
  }
}

// Helper function to find payment_restrictions_section in campaign data
const findPaymentRestrictionsSection = (node) => {
  if (!node) return null
  
  if (node.keyName === 'payment_restrictions_section') {
    return node
  }
  
  if (node.children && Array.isArray(node.children)) {
    for (const child of node.children) {
      const found = findPaymentRestrictionsSection(child)
      if (found) return found
    }
  }
  
  if (node.catalogComponent) {
    return findPaymentRestrictionsSection(node.catalogComponent)
  }
  
  return null
}

// Helper function to find payment_restrictions_section in payment catalog
// Payment catalog might return the section directly or wrapped
const findPaymentRestrictionsInCatalog = (data) => {
  if (!data) return null
  
  // If it's the section directly
  if (data.keyName === 'payment_restrictions_section') {
    return data
  }
  
  // If it's wrapped in a structure, search recursively
  if (data.children && Array.isArray(data.children)) {
    for (const child of data.children) {
      const found = findPaymentRestrictionsInCatalog(child)
      if (found) return found
    }
  }
  
  // Check if it's wrapped in catalogComponent
  if (data.catalogComponent) {
    return findPaymentRestrictionsInCatalog(data.catalogComponent)
  }
  
  return null
}

const getCampaignById = async ({
  campaignId
}) => {
  try {
    const teamId = getTeamId();
    const response = await axiosInstance.get(`${config.BENEFIT_ENGINE.BASE}/capture/CAMPAIGN/${campaignId}`, {
      params: {
        teamId
      }
    })
    
    // Also fetch payment catalog to get possibleValues
    try {
      const paymentCatalogResponse = await getPaymentCatalog()
      
      // Find payment_restrictions_section in both responses
      const campaignPaymentSection = findPaymentRestrictionsSection(response.data)
      const paymentCatalogSection = findPaymentRestrictionsInCatalog(paymentCatalogResponse)
      
      // Merge possibleValues from payment catalog into campaign data
      if (campaignPaymentSection && paymentCatalogSection) {
        mergePossibleValues(campaignPaymentSection, paymentCatalogSection)
      }
    } catch (paymentErr) {
      console.log("getPaymentCatalog err (non-fatal): ", paymentErr)
      // Continue even if payment catalog fetch fails
    }
    
    return response.data
  } catch (err) {
    console.log("getCampaignById err: ", err)
    throw err
  }
}

const deleteCampaignById = async ({
  campaignId,
  catalogId //For now hardcoded as: CAMPAIGN_<type_of_campaign>
}) => {
  try {
    const teamId = getTeamId();
    const response = await axiosInstance.delete(`${config.BENEFIT_ENGINE.BASE}/capture/CAMPAIGN`, {
      data: {
        catalogInstanceDetails: {
          catalogInstanceId: campaignId,
          teamId
        },
        catalogComponent: null
      },
      params: {
        teamId
      }
    })
    return response?.data
  } catch (err) {
    console.log("deleteCampaignById err: ", err)
  }
}

const updateCampaign = async ({
  dataJson
}) => {
  try {
    const teamId = getTeamId();
    const teamName = getTeamName();
    const response = await axiosInstance.put(`${config.BENEFIT_ENGINE.BASE}/capture/CAMPAIGN`, {
      ...dataJson,
      catalogInstanceDetails: {
        ...dataJson.catalogInstanceDetails,
        teamId,
        teamName
      }
    }, {
      params: {
        teamId
      }
    })
    return response.data
  } catch (err) {
    console.log("createConfig err: ", err)
    throw err
  }
}

//fetches the base benefit config
const getBasebenefit = async ({
  catalogType,
  catalogId,
  categoryType //type of rule selected previously
}) => {
  try {
    const teamId = getTeamId();
    const response = await axiosInstance.post(`${config.BENEFIT_ENGINE.BASE}/catalog/get`, {
      catalogType,
      catalogId,
      teamId,
      benefitCatalog: {
        categoryType,
      }
    }, {
      params: {
        teamId
      }
    })

    return response.data
  } catch (err) {
    console.log("getBasebenefit err: ", err)
    throw err
  }
}

//fetches the Benefit Base for the chosen Benefit type
const getBenefitBaseWithType = async ({
  catalogType,
  catalogId,
  categoryType, //type of rule selected previously
  benefitType
}) => {
  try {
    const teamId = getTeamId();
    const response = await axiosInstance.post(`${config.BENEFIT_ENGINE.BASE}/catalog/get`, {
      catalogType,
      catalogId,
      teamId,
      benefitCatalog: {
        categoryType,
        benefitType
      }
    }, {
      params: {
        teamId
      }
    })

    return response.data

  } catch (err) {
    console.log("getBenefitBaseWithType err: ", err)
    throw err
  }
}

const getBenefitBaseWithBenfitRewardType = async ({
  catalogType,
  catalogId,
  categoryType, //type of rule selected previously
  benefitType,
  rewardType
}) => {
  try {
    const teamId = getTeamId();
    const response = await axiosInstance.post(`${config.BENEFIT_ENGINE.BASE}/catalog/get`, {
      catalogType,
      catalogId,
      teamId,
      benefitCatalog: {
        categoryType,
        benefitType,
        rewardType,
      }
    },{
      params: {
        teamId
      }
    })
    return response.data
  } catch (err) {
    console.log("getBenefitBaseWithBenfitRewardType err: ", err)
    throw err
  }
}

const createRule = async ({
  catalogState, // DRAFT | PUBLISHED
  catalogComponent, // UI Config Json
}) => {
  try {
    const teamId = getTeamId();
    const response = await axiosInstance.post(`${config.BENEFIT_ENGINE.BASE}/capture/RULE`, {
      catalogInstanceDetails: {
        catalogState,  // in update call pass "ruleId":[]
        teamId
      },
      catalogComponent
    },{
      params: {
        teamId
      }
    })

    return response.data

  } catch (err) {
    console.log("createConfig err: ", err)
  }
}

const updateRule = async ({
  dataJson
}) => {
  try {
    const teamId = getTeamId();
    const response = await axiosInstance.put(`${config.BENEFIT_ENGINE.BASE}/capture/RULE`, {
      ...dataJson,
      catalogInstanceDetails: {
        ...dataJson.catalogInstanceDetails,
        teamId
      }
    },{
      params: {
        teamId
      }
    })
    return response.data
  } catch (err) {
    console.log("updateRule err: ", err)
    throw err
  }
}

const getRuleById = async ({
  ruleId
}) => {
  try {
    const teamId = getTeamId();
    const response = await axiosInstance.get(`${config.BENEFIT_ENGINE.BASE}/capture/RULE/${ruleId}`, {
      params: {
        teamId
      }
    })
    return response.data
  } catch (err) {
    console.log("getRuleById err: ", err)
    throw err
  }
}

// Utility function to sort possibleValues based on topValues for MULTI_SELECT_DROPDOWN fields
const sortPossibleValuesByTopValues = (node) => {
  if (!node || typeof node !== 'object') return;
  
  // If this is an element with MULTI_SELECT_DROPDOWN and has topValues
  if (node.fieldType === 'MULTI_SELECT_DROPDOWN' && 
      node.valueConfig && 
      node.valueConfig.possibleValues && 
      node.valueConfig.topValues) {
    
    const { possibleValues, topValues } = node.valueConfig;
    
    // Create a map for quick lookup
    const valueMap = new Map();
    possibleValues.forEach(val => {
      valueMap.set(val.id, val);
    });
    
    // Separate top values and non-top values
    const topValuesArray = [];
    const nonTopValuesArray = [];
    const topValuesSet = new Set(topValues);
    
    // First, add items in topValues order
    topValues.forEach(id => {
      if (valueMap.has(id)) {
        topValuesArray.push(valueMap.get(id));
        valueMap.delete(id); // Remove from map so we don't add it again
      }
    });
    
    // Then, add remaining items in their original order
    possibleValues.forEach(val => {
      if (!topValuesSet.has(val.id)) {
        nonTopValuesArray.push(val);
      }
    });
    
    // Combine: top values first, then non-top values
    node.valueConfig.possibleValues = [...topValuesArray, ...nonTopValuesArray];
  }
  
  // Recursively process children
  if (Array.isArray(node.children)) {
    node.children.forEach(child => sortPossibleValuesByTopValues(child));
  }
}

const getPaymentCatalog = async () => {
  try {
    const teamId = getTeamId();
    const response = await axiosInstance.get(`${config.BENEFIT_ENGINE.BASE}/resource/UIConfig?catalogId=PAYMENT&teamId=${teamId}`)

    const data = response.data;
    
    // Process the response to sort possibleValues based on topValues
    // Handle both direct response and wrapped in catalogComponent
    if (data) {
      if (data.catalogComponent) {
        sortPossibleValuesByTopValues(data.catalogComponent);
      } else {
        sortPossibleValuesByTopValues(data);
      }
    }

    return data
  } catch (err) {
    console.log("getPaymentCatalog err: ", err)
    throw err
  }
}

const deleteRuleById = async ({
  ruleId,
  catalogId //For now hardcoded as: CAMPAIGN_<type_of_campaign>
}) => {
  try {
    const teamId = getTeamId();
    const response = await axiosInstance.delete(`${config.BENEFIT_ENGINE.BASE}/capture/RULE`, {
      data: {
        catalogInstanceDetails: {
          catalogInstanceId: ruleId,
          teamId
        },
        catalogComponent: null
      },
      params: {
        teamId
      }
    })
    return response?.data
  } catch (err) {
    console.log("deleteRuleById err: ", err)
  }
}

export default {
  getAllCampaigns,
  getUIConfig,
  getPaymentCatalog,
  getUIConfigV2,
  createConfigV2,
  createConfig,
  getCampaignById,
  deleteCampaignById,
  getAllRules,
  updateCampaign,
  getBasebenefit,
  getBenefitBaseWithType,
  getBenefitBaseWithBenfitRewardType,
  createRule,
  updateRule,
  getRuleById,
  deleteRuleById
}